import type { LegPriceBreak } from '@erp/shared-types';

/**
 * The offer the board makes a buyer when one more step up the vendor's
 * all-units break table is worth taking: how far short of it the line sits,
 * where it lands, and what the whole line saves there.
 */
export interface PriceBreakNudge {
  /** Units to add to the current quantity to reach `targetQuantity`. */
  addUnits: number;
  /**
   * The next break rounded up to the vendor's MOQ and order multiple: the
   * quantity the server orders when the buyer takes the nudge.
   */
  targetQuantity: number;
  /**
   * Unit cost every unit on the line drops to at `targetQuantity`. Can be a
   * deeper tier than the next one when the rounding crosses another break.
   */
  nextUnitCost: number;
  /** All-units saving on the whole line at `targetQuantity`, always positive. */
  savings: number;
}

/**
 * Resolve the price-break provenance the engine froze onto a buy leg against
 * the quantity actually on the line right now, or null when there is nothing
 * worth showing.
 *
 * Reads the lattice-aware `nudge*` fields, never `nextTier*`: a break boundary
 * is the vendor's price-list quantity and is not always one it ships (23 on a
 * multiple of 5), and the price at the rounded quantity can be a deeper tier.
 *
 * Deliberately silent when:
 * - the leg carries no provenance, or no nudge (a leg the engine never priced,
 *   a buyer-composed plan, a line already at the vendor's deepest break, or a
 *   leg frozen before the nudge was made lattice-aware);
 * - the provenance does not name the vendor row it was priced on. The server
 *   applies the nudge on that row and refuses without it, so offering the
 *   chip would only ever end in a refusal;
 * - the saving is not strictly positive. The engine already drops those, but
 *   `nudgeSavings` is an all-units comparison and vendor tables are not always
 *   monotonic, so the chip does not take that on trust;
 * - the line is already at or past the target, which the frozen provenance
 *   cannot tell us because it was computed at the engine's quantity rather than
 *   the current one.
 */
export function resolvePriceBreakNudge({
  priceBreak,
  quantity,
}: {
  priceBreak: LegPriceBreak | null | undefined;
  quantity: number | null | undefined;
}): PriceBreakNudge | null {
  if (priceBreak == null) return null;

  const { nudgeQuantity, nudgeUnitCost, nudgeSavings, supplierProductId } =
    priceBreak;
  if (nudgeQuantity == null || nudgeUnitCost == null) return null;
  if (supplierProductId == null) return null;
  if (nudgeSavings == null || nudgeSavings <= 0) return null;
  if (quantity == null || !Number.isFinite(quantity)) return null;

  const addUnits = nudgeQuantity - quantity;
  if (addUnits <= 0) return null;

  return {
    addUnits,
    targetQuantity: nudgeQuantity,
    nextUnitCost: nudgeUnitCost,
    savings: nudgeSavings,
  };
}
